import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import {
  AlertTriangle,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Target,
  Building2,
  Truck,
  Layers,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Bell,
  BellRing,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const DependencyHealthScore = () => {
  const { api } = useAuth();
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRisks, setExpandedRisks] = useState({});
  const [alertsData, setAlertsData] = useState(null);

  const fetchHealthScore = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/dependencies/health-score");
      setHealthData(response.data);
      
      // Also fetch alert history
      try {
        const alertsResponse = await api.get("/dependencies/health-score/alerts?limit=5");
        setAlertsData(alertsResponse.data);
      } catch {
        // Alert history is optional, don't fail the component
      }
    } catch (error) {
      console.error("Failed to fetch health score:", error);
      toast.error("Failed to load dependency health score");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchHealthScore();
  }, [fetchHealthScore]);

  const toggleRisk = (index) => {
    setExpandedRisks((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getGradeColor = (grade) => {
    const colors = {
      A: "bg-emerald-500",
      B: "bg-green-500",
      C: "bg-yellow-500",
      D: "bg-sky-700",
      F: "bg-red-500",
    };
    return colors[grade] || "bg-slate-500";
  };

  const getGradeIcon = (grade) => {
    if (grade === "A") return <ShieldCheck className="w-8 h-8 text-emerald-500" />;
    if (grade === "B") return <Shield className="w-8 h-8 text-green-500" />;
    if (grade === "C") return <ShieldAlert className="w-8 h-8 text-yellow-500" />;
    return <ShieldX className="w-8 h-8 text-red-500" />;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: "bg-red-100 text-red-800 border-red-200",
      high: "bg-sky-100 text-sky-800 border-sky-200",
      medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
      low: "bg-blue-100 text-blue-800 border-blue-200",
    };
    return colors[severity] || "bg-slate-100 text-slate-800";
  };

  const getRiskIcon = (riskType) => {
    const icons = {
      spof: <AlertTriangle className="w-5 h-5" />,
      critical_path: <Target className="w-5 h-5" />,
      concentration: <Building2 className="w-5 h-5" />,
      missing_backup: <AlertCircle className="w-5 h-5" />,
    };
    return icons[riskType] || <AlertTriangle className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <Card data-testid="dependency-health-loading">
        <CardContent className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400 mr-2" />
          <span className="text-slate-500">Analyzing dependency health...</span>
        </CardContent>
      </Card>
    );
  }

  if (!healthData) {
    return (
      <Card data-testid="dependency-health-error">
        <CardContent className="flex items-center justify-center py-12">
          <AlertCircle className="w-6 h-6 text-red-500 mr-2" />
          <span className="text-slate-500">Failed to load health score</span>
          <Button variant="outline" size="sm" className="ml-4" onClick={fetchHealthScore}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="dependency-health-score">
      {/* Overall Score Card */}
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-2">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {getGradeIcon(healthData.grade)}
                Dependency Health Score
              </CardTitle>
              <CardDescription>
                Analysis of your BIA dependency risks and single points of failure
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchHealthScore} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-8">
            {/* Score Circle */}
            <div className="relative">
              <div
                className={`w-28 h-28 rounded-full flex items-center justify-center ${getGradeColor(healthData.grade)} text-white`}
                data-testid="health-score-circle"
              >
                <div className="text-center">
                  <div className="text-4xl font-bold">{healthData.grade}</div>
                  <div className="text-sm opacity-90">{healthData.overall_score}/100</div>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-slate-800">
                  {healthData.summary.total_processes}
                </div>
                <div className="text-xs text-slate-500">Total Processes</div>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-red-600">
                  {healthData.summary.tier_1_processes}
                </div>
                <div className="text-xs text-slate-500">Tier 1 (Critical)</div>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-slate-800">
                  {healthData.summary.risks_found}
                </div>
                <div className="text-xs text-slate-500">Risks Identified</div>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-sky-700">
                  {healthData.summary.critical_risks + healthData.summary.high_risks}
                </div>
                <div className="text-xs text-slate-500">High Priority</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Health Score</span>
              <span>{healthData.overall_score}%</span>
            </div>
            <Progress value={healthData.overall_score} className="h-2" />
          </div>

          {/* Dependency Counts */}
          <div className="mt-4 flex flex-wrap gap-3">
            <Badge variant="outline" className="gap-1">
              <Truck className="w-3 h-3" />
              {healthData.summary.unique_vendors} Vendors
            </Badge>
            <Badge variant="outline" className="gap-1">
              <Building2 className="w-3 h-3" />
              {healthData.summary.unique_facilities} Facilities
            </Badge>
            <Badge variant="outline" className="gap-1">
              <Layers className="w-3 h-3" />
              {healthData.summary.unique_applications} Applications
            </Badge>
          </div>
          
          {/* Alert Status */}
          <div className="mt-4 p-3 bg-slate-50 rounded-lg border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {healthData.overall_score < 70 ? (
                  <BellRing className="w-4 h-4 text-red-500" />
                ) : (
                  <Bell className="w-4 h-4 text-slate-400" />
                )}
                <span className="text-sm font-medium">
                  {healthData.overall_score < 70 ? "Alerts Enabled" : "Monitoring Active"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Mail className="w-3 h-3" />
                {alertsData?.count > 0 ? (
                  <span>{alertsData.count} alert(s) sent</span>
                ) : (
                  <span>No alerts sent</span>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              BCM Managers receive email alerts when score drops below Grade C (70/100)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Risks Section */}
      {healthData.risks.length > 0 && (
        <Card data-testid="risks-section">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Identified Risks ({healthData.risks.length})
            </CardTitle>
            <CardDescription>
              Address these risks to improve your dependency health score
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {healthData.risks.map((risk, index) => (
              <div
                key={index}
                className={`border rounded-lg overflow-hidden ${getSeverityColor(risk.severity)}`}
                data-testid={`risk-item-${index}`}
              >
                <div
                  className="p-4 cursor-pointer flex items-center justify-between"
                  onClick={() => toggleRisk(index)}
                >
                  <div className="flex items-center gap-3">
                    {getRiskIcon(risk.risk_type)}
                    <div>
                      <div className="font-medium">{risk.title}</div>
                      <div className="text-xs opacity-75">
                        {risk.affected_processes.length} process(es) affected
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`text-xs uppercase ${
                        risk.severity === "critical"
                          ? "bg-red-200"
                          : risk.severity === "high"
                          ? "bg-sky-200"
                          : "bg-yellow-200"
                      }`}
                    >
                      {risk.severity}
                    </Badge>
                    {expandedRisks[index] ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </div>
                </div>

                {expandedRisks[index] && (
                  <div className="px-4 pb-4 border-t bg-white/50">
                    <p className="text-sm mt-3 mb-3">{risk.description}</p>

                    <div className="mb-3">
                      <div className="text-xs font-semibold mb-1">Affected Processes:</div>
                      <div className="flex flex-wrap gap-1">
                        {risk.affected_processes.map((proc, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {proc}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-semibold mb-1">Recommendations:</div>
                      <ul className="text-sm space-y-1">
                        {risk.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <ArrowRight className="w-3 h-3 mt-1 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Improvement Actions */}
      {healthData.improvement_actions.length > 0 && (
        <Card data-testid="improvement-actions">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              Improvement Actions
            </CardTitle>
            <CardDescription>Prioritized actions to strengthen your dependency resilience</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {healthData.improvement_actions.map((action, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                  data-testid={`action-item-${index}`}
                >
                  <Badge
                    className={`mt-0.5 ${
                      action.priority === "P0"
                        ? "bg-red-500"
                        : action.priority === "P1"
                        ? "bg-sky-700"
                        : "bg-blue-500"
                    }`}
                  >
                    {action.priority}
                  </Badge>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{action.action}</div>
                    <div className="text-xs text-slate-500 mt-1">{action.description}</div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {action.effort} Effort
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Risks State */}
      {healthData.risks.length === 0 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="py-8 text-center">
            <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h3 className="font-semibold text-emerald-800">Excellent Dependency Health!</h3>
            <p className="text-sm text-emerald-600 mt-1">
              No critical dependency risks identified. Continue monitoring your dependency map.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DependencyHealthScore;
